import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { isToday } from 'date-fns';
import {
    fetchOrders,
    printShippingLabel as printShippingLabelApi,
    batchPrintShippingLabels,
    fetchTodayOrderStats,
    ShippingLabelResponse,
    BatchShippingLabelResponse,
} from '../../../api/orders';
import { fetchStickerInventory } from '../../../api/ems';
import { printShippingLabel } from '../../../components/PrintableShippingLabel';
import { mapApiOrderToView } from '../utils/mappers';
import { Order } from '../types';
import { logger } from '../../../utils/logger';

// Helper function to open PDF base64 in a new tab
const openPdfInNewTab = (base64Data: string): boolean => {
    try {
        if (!base64Data) {
            logger.error('[openPdfInNewTab] No base64 data provided');
            return false;
        }

        // Clean up base64 string (remove potential whitespace/newlines)
        const cleanedBase64 = base64Data.trim().replace(/\s/g, '');

        const binaryString = window.atob(cleanedBase64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);

        logger.debug('[openPdfInNewTab] Opening PDF URL:', url);
        const printWindow = window.open(url, '_blank');

        if (printWindow) {
            // Keep URL alive for a bit to allow the window to load
            setTimeout(() => URL.revokeObjectURL(url), 10000);
            return true;
        } else {
            logger.warn('[openPdfInNewTab] Popup blocked or failed to open');
            URL.revokeObjectURL(url);
            return false;
        }
    } catch (error) {
        logger.error('[openPdfInNewTab] Error:', error);
        return false;
    }
};

export function useOrderManagement() {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(new Set());
    const [isBatchMode, setIsBatchMode] = useState(false);

    const ordersQuery = useQuery({
        queryKey: ['orders'],
        queryFn: () => fetchOrders(50, 1),
        refetchInterval: 60000,
        staleTime: 30000,
    });

    const todayStatsQuery = useQuery({
        queryKey: ['todayOrderStats'],
        queryFn: fetchTodayOrderStats,
        refetchInterval: 60000,
        staleTime: 30000,
    });

    const emsInventoryQuery = useQuery({
        queryKey: ['emsInventory'],
        queryFn: fetchStickerInventory,
        refetchInterval: 60000,
        staleTime: 30000,
    });

    const orders = useMemo(() => (ordersQuery.data || []).map(mapApiOrderToView), [ordersQuery.data]);

    const filteredOrders = useMemo(() => {
        return orders.filter(order => {
            const isPaidOrder = ['paid', 'printed', 'shipped', 'delivered'].includes(order.status);
            if (!isPaidOrder) return false;

            const matchesSearch =
                order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                order.customerPhone.includes(searchTerm);

            const matchesStatus = statusFilter === 'all' || order.status === statusFilter;

            return matchesSearch && matchesStatus;
        });
    }, [orders, searchTerm, statusFilter]);

    const pendingOrders = useMemo(() =>
        filteredOrders.filter(order => order.status === 'paid')
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        , [filteredOrders]);

    const printedOrdersToday = useMemo(() =>
        filteredOrders.filter(order => ['printed', 'shipped', 'delivered'].includes(order.status) && isToday(new Date(order.createdAt)))
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        , [filteredOrders]);

    const printLabelMutation = useMutation({
        mutationFn: (orderId: string) => printShippingLabelApi(orderId),
        onSuccess: (data: ShippingLabelResponse, orderId) => {
            logger.debug('[DEBUG] ShippingLabelResponse:', data);
            const trackingNumber = data?.label?.trackingNumber;
            const stickerCode = data?.sticker?.stickerCode || data?.label?.stickerCode;

            const successMessageParts: string[] = [];
            successMessageParts.push('สร้างใบปะหน้าสำเร็จ');
            if (trackingNumber) successMessageParts.push(`เลขติดตาม ${trackingNumber}`);
            if (stickerCode) successMessageParts.push(`สติกเกอร์ EMS ${stickerCode}`);

            toast.success(successMessageParts.join(' • '));

            const mergedPdf = data?.label?.mergedPdfBase64;
            const fallbackPdf = data?.label?.labelBase64;

            if (mergedPdf || fallbackPdf) {
                const pdfToUse = mergedPdf || fallbackPdf!;
                const opened = openPdfInNewTab(pdfToUse);
                if (!opened) {
                    toast.error('ไม่สามารถเปิด PDF สำหรับพิมพ์ได้');
                    const order = ordersQuery.data?.find(o => o.id === orderId);
                    if (order) printShippingLabel(order, data);
                }
            } else {
                const order = ordersQuery.data?.find(o => o.id === orderId);
                if (order) printShippingLabel(order, data);
            }

            queryClient.invalidateQueries(['orders']);
            if (selectedOrder && selectedOrder.id === orderId) {
                setSelectedOrder(prev => (prev ? {
                    ...prev,
                    status: 'printed',
                    shippingLabelPrinted: true,
                    trackingNumber,
                } : prev));
            }
        },
        onError: (error: any) => {
            const errData = error?.response?.data?.error;
            const message = typeof errData === 'string' ? errData : errData?.message || 'เกิดข้อผิดพลาดในการปริ้นใบขนส่ง';
            toast.error(message);
        },
    });

    const batchPrintMutation = useMutation({
        mutationFn: (orderIds: string[]) => batchPrintShippingLabels(orderIds),
        onSuccess: (data: BatchShippingLabelResponse) => {
            const { summary, results, mergedPdfBase64: mergedPdf } = data;
            toast.success(`สร้างใบปะหน้าเสร็จสิ้น: สำเร็จ ${summary.successful} รายการ${summary.failed > 0 ? `, ล้มเหลว ${summary.failed} รายการ` : ''}`);

            if (mergedPdf && results.successful.length > 0) {
                const opened = openPdfInNewTab(mergedPdf);
                if (!opened) {
                    toast.error('ไม่สามารถเปิด PDF สำหรับพิมพ์ได้');
                }
            }

            if (results.failed.length > 0) {
                results.failed.forEach(failed => {
                    toast.error(`คำสั่งซื้อ ${failed.orderId}: ${failed.error}`);
                });
            }

            queryClient.invalidateQueries(['orders']);
            setSelectedOrderIds(new Set());
            setIsBatchMode(false);
        },
        onError: (error: any) => {
            const errData = error?.response?.data?.error;
            const message = typeof errData === 'string' ? errData : errData?.message || 'เกิดข้อผิดพลาดในการปริ้นใบขนส่งแบบหลายรายการ';
            toast.error(message);
        },
    });

    const handleToggleSelectOrder = (orderId: string | number) => {
        setSelectedOrderIds(prev => {
            const newSet = new Set(prev);
            const idStr = String(orderId);
            if (newSet.has(idStr)) {
                newSet.delete(idStr);
            } else {
                newSet.add(idStr);
            }
            return newSet;
        });
    };

    const handleSelectAllPending = () => {
        const pendingPaidOrders = pendingOrders.map(o => o.id);
        setSelectedOrderIds(new Set(pendingPaidOrders));
    };

    const handleClearSelection = () => {
        setSelectedOrderIds(new Set());
    };

    const handleBatchPrint = () => {
        if (selectedOrderIds.size === 0) {
            toast.error('กรุณาเลือกคำสั่งซื้อที่ต้องการปริ้น');
            return;
        }
        batchPrintMutation.mutate(Array.from(selectedOrderIds));
    };

    return {
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        selectedOrder,
        setSelectedOrder,
        selectedOrderIds,
        isBatchMode,
        setIsBatchMode,
        orders: ordersQuery.data,
        isLoading: ordersQuery.isLoading,
        error: ordersQuery.error,
        refetch: ordersQuery.refetch,
        todayStats: todayStatsQuery.data,
        emsInventory: emsInventoryQuery.data,
        pendingOrders,
        printedOrdersToday,
        printLabelMutation,
        batchPrintMutation,
        handleToggleSelectOrder,
        handleSelectAllPending,
        handleClearSelection,
        handleBatchPrint,
    };
}
